/** @format */
import { css } from 'styled-components';

import { CollapsibleAsideTitleColor, CollapsibleAsideVariant } from '.';

export type CollapsibleAsideStyleProps = {
    isOpen: boolean;
    variant: CollapsibleAsideVariant;
    $titleLocatedAtTop: boolean;
    $titleElementColor?: CollapsibleAsideTitleColor;
};

/**
 * Base styles for the collapsible aside component
 * */
const baseStyles = ({ $titleLocatedAtTop }: CollapsibleAsideStyleProps) => css`
    aside {
        .title {
            cursor: pointer;
            display: flex;
            align-items: center;

            svg {
                transition: 0.3s ease-in-out transform;
            }
        }

        .content-container,
        .content {
            overflow: hidden;
        }

        .content {
            padding-top: ${$titleLocatedAtTop ? 'var(--layout-spacing-24)' : 0};
        }
    }
`;

/**
 * Variant styles for the collapsible aside component
 *
 * @param {CollapsibleAsideStyleProps} props The collapsible aside properties object
 * */
const variantStyles = ({ variant, isOpen }: CollapsibleAsideStyleProps) => {
    switch (variant) {
        case CollapsibleAsideVariant.INLINE:
            return css`
                aside {
                    .collapsible-aside-title-container {
                        height: 3.6rem;
                        display: flex;
                        align-items: center;
                    }

                    .title {
                        display: inline-flex;
                        position: relative;

                        .toggle-button {
                            color: inherit;
                            position: absolute;
                            top: 50%;
                            transform: translateY(-50%);
                            fill: currentColor;
                            /* Width of the svg is actually 12px but has the left padding to sit flush next to the text */
                            width: 2rem;
                            padding-left: 0.8rem;
                            left: 100%;
                            height: 100%;

                            svg {
                                transform: scale(1, ${isOpen ? 1 : -1});
                                display: block;
                                width: 100%;
                                height: 100%;
                            }
                        }
                    }
                }
            `;
        case CollapsibleAsideVariant.STANDALONE:
        default:
            return css`
                aside {
                    .title {
                        justify-content: space-between;

                        svg {
                            transform: rotate(90deg)
                                scale(${isOpen ? -1 : 1}, 1);
                        }
                    }
                }
            `;
    }
};

const chooseColor = (color: CollapsibleAsideTitleColor) => {
    switch (color) {
        case CollapsibleAsideTitleColor.MIDNIGHT:
            return 'var(--colors-text-secondary)';
        default:
            return 'inherit';
    }
};

const titleElementColorStyles = ({
    $titleElementColor,
}: CollapsibleAsideStyleProps) => {
    if (!$titleElementColor) return;

    return css`
        aside {
            display: contents;

            .title {
                &::after {
                    display: none;
                }
                span {
                    background-image: none !important;
                    margin-bottom: 0 !important;
                    padding-bottom: 0 !important;
                    -webkit-text-fill-color: initial;
                    color: ${chooseColor($titleElementColor)};
                    text-fill-color: initial;
                    text-decoration: underline;
                }
            }
        }
    `;
};

/**
 * The collapsible aside component styles
 *
 * @param {CollapsibleAsideStyleProps} props The collapsible aside properties object
 * */
export const styles = (props: CollapsibleAsideStyleProps) => css`
    ${baseStyles(props)}
    ${variantStyles(props)}
    ${titleElementColorStyles(props)}
`;
